import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";

let statusBarOverlayActive = false;

export async function configureNativeStatusBar(isDark: boolean): Promise<void> {
  if (!isNativeCapacitorApp()) return;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setOverlaysWebView({ overlay: statusBarOverlayActive });
    if (!statusBarOverlayActive) {
      await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
      await StatusBar.setBackgroundColor({
        color: isDark ? "#000000" : "#ffffff",
      });
    }
  } catch {
    /* plugin unavailable */
  }
}

/** Draw WebView edge-to-edge under status bar (e.g. CarDetails gallery). */
export async function setNativeStatusBarOverlay(overlay: boolean): Promise<void> {
  if (!isNativeCapacitorApp()) return;
  statusBarOverlayActive = overlay;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setOverlaysWebView({ overlay });
    if (overlay) {
      await StatusBar.setStyle({ style: Style.Dark });
    }
  } catch {
    /* plugin unavailable */
  }
}

export async function configureNativeKeyboard(): Promise<void> {
  if (!isNativeCapacitorApp()) return;
  try {
    const { Capacitor } = await import("@capacitor/core");
    const { Keyboard, KeyboardResize } = await import("@capacitor/keyboard");
    const platform = Capacitor.getPlatform();

    if (platform === "ios") {
      try {
        await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
      } catch {
        try {
          await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
        } catch {
          /* optional */
        }
      }
      try {
        await Keyboard.setAccessoryBarVisible({ isVisible: true });
      } catch {
        /* optional */
      }
      try {
        await Keyboard.setScroll({ isDisabled: false });
      } catch {
        /* optional */
      }
      return;
    }

    if (platform === "android") {
      try {
        await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
      } catch {
        /* optional */
      }
    }
  } catch {
    /* plugin unavailable */
  }
}

/** Fire haptic from a click handler (dynamic import, no await before gesture ends). */
export function hapticImpactSync(
  style: "light" | "medium" | "heavy" = "light",
): void {
  if (!isNativeCapacitorApp()) return;
  void import("@capacitor/haptics")
    .then(({ Haptics, ImpactStyle }) => {
      const map = {
        light: ImpactStyle.Light,
        medium: ImpactStyle.Medium,
        heavy: ImpactStyle.Heavy,
      } as const;
      return Haptics.impact({ style: map[style] });
    })
    .catch(() => undefined);
}

/** Stronger alert haptic (outbid / win) — NotificationType is more noticeable than impact. */
export function hapticNotificationSync(
  type: "success" | "warning" | "error" = "warning",
): void {
  if (!isNativeCapacitorApp()) return;
  void import("@capacitor/haptics")
    .then(({ Haptics, NotificationType }) => {
      const map = {
        success: NotificationType.Success,
        warning: NotificationType.Warning,
        error: NotificationType.Error,
      } as const;
      return Haptics.notification({ type: map[type] });
    })
    .catch(() => undefined);
}

export async function hapticImpact(
  style: "light" | "medium" | "heavy" = "light"
): Promise<void> {
  if (!isNativeCapacitorApp()) return;
  hapticImpactSync(style);
}

export async function hapticSelection(): Promise<void> {
  if (!isNativeCapacitorApp()) return;
  try {
    const { Haptics } = await import("@capacitor/haptics");
    await Haptics.selectionStart();
    await Haptics.selectionChanged();
    await Haptics.selectionEnd();
  } catch {
    /* ignore */
  }
}

export type NetworkConnected = boolean;

export async function getNetworkStatus(): Promise<NetworkConnected> {
  if (!isNativeCapacitorApp()) return navigator.onLine;
  try {
    const { Network } = await import("@capacitor/network");
    const status = await Network.getStatus();
    return status.connected;
  } catch {
    return navigator.onLine;
  }
}

export function subscribeNetworkStatus(
  onChange: (connected: boolean) => void
): () => void {
  if (!isNativeCapacitorApp()) {
    const handler = () => onChange(navigator.onLine);
    window.addEventListener("online", handler);
    window.addEventListener("offline", handler);
    return () => {
      window.removeEventListener("online", handler);
      window.removeEventListener("offline", handler);
    };
  }

  let disposed = false;
  let removeNative: (() => void) | undefined;

  void import("@capacitor/network").then(async ({ Network }) => {
    if (disposed) return;
    try {
      const status = await Network.getStatus();
      onChange(status.connected);
      const listener = await Network.addListener(
        "networkStatusChange",
        (s) => onChange(s.connected)
      );
      removeNative = () => {
        void listener.remove();
      };
    } catch {
      /* fall back to window online/offline only */
    }
  });

  const onOnline = () => onChange(true);
  const onOffline = () => onChange(false);
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);

  return () => {
    disposed = true;
    removeNative?.();
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
  };
}
